import api from './api';

export interface DlpReportAttachment {
  id: number;
  period: string;
  companyName: string;
  linkId: string;
  linkName: string;
  linkUrl: string;
  updateDate: Date;
  updateUser: string;
}

// 세아제강 계열 DLP 운영보고서 (IPT-441) — Privacy-i 콘솔 커스텀 리포트 캐시 조회 결과.
// 표마다 컬럼 구성이 달라 row는 임의 키를 갖는 레코드 배열로 둔다.
export interface DlpSteelReport {
  period: string;
  company: string;
  baseDate: string | null;
  reports: {
    print: Record<string, Record<string, string>[]>;
    upload: Record<string, Record<string, string>[]>;
    copy: Record<string, Record<string, string>[]>;
  };
}

export interface DlpBuyLicense {
  id: number;
  orgCode: string;
  companyName: string;
  amount: number;
  updateDate: Date;
  updateUser: string;
  before: number;
  after: number;
}

export const dlpService = {
  // Period 관련
  /** 회사를 주면 그 회사의 데이터가 있는 달만 (Viewer는 발행분만). 회사 없이는 관리자 화면용 전체 기간 */
  getAvailablePeriods: async (companyName?: string): Promise<string[]> => {
    const response = await api.get<string[]>('/dlp/report/periods', {
      params: companyName ? { companyName } : {},
    });
    return response.data;
  },

  getLatestPeriod: async (companyName?: string): Promise<string | null> => {
    const response = await api.get<{ period: string | null }>('/dlp/report/latest-period', {
      params: companyName ? { companyName } : {},
    });
    return response.data.period;
  },

  // 인가된 회사 목록
  getPermittedCompanies: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/dlp/report/permitted-companies');
    return response.data;
  },

  // Publish 상태
  getPublishStatus: async (period: string, companyName: string): Promise<boolean> => {
    const response = await api.get<{ published: boolean }>('/dlp/report/publish-status', {
      params: { period, companyName },
    });
    return response.data.published;
  },

  // Attachments
  getAttachments: async (
    period: string,
    companyName: string,
  ): Promise<DlpReportAttachment[]> => {
    const response = await api.get<DlpReportAttachment[]>('/dlp/attachments', {
      params: { period, companyName },
    });
    return response.data;
  },

  createAttachment: async (
    attachment: Partial<DlpReportAttachment>,
  ): Promise<DlpReportAttachment> => {
    const response = await api.post<DlpReportAttachment>('/dlp/attachments', attachment);
    return response.data;
  },

  updateAttachment: async (
    id: number,
    updateData: Partial<DlpReportAttachment>,
  ): Promise<DlpReportAttachment> => {
    const response = await api.patch<DlpReportAttachment>(`/dlp/attachments/${id}`, updateData);
    return response.data;
  },

  deleteAttachment: async (id: number): Promise<void> => {
    await api.delete(`/dlp/attachments/${id}`);
  },

  // Licenses
  getAllLicenses: async (): Promise<DlpBuyLicense[]> => {
    const response = await api.get<DlpBuyLicense[]>('/dlp/licenses');
    return response.data;
  },

  getLicense: async (companyName: string): Promise<DlpBuyLicense | null> => {
    const response = await api.get<DlpBuyLicense>(`/dlp/licenses/${companyName}`);
    return response.data;
  },

  createLicense: async (license: Partial<DlpBuyLicense>): Promise<DlpBuyLicense> => {
    const response = await api.post<DlpBuyLicense>('/dlp/licenses', license);
    return response.data;
  },

  // Report Summary
  getSummary: async (period: string, companyName: string): Promise<any> => {
    const response = await api.get('/dlp/report/summary', {
      params: { period, companyName },
    });
    return response.data;
  },

  saveSummary: async (
    period: string,
    companyName: string,
    summaryContent: string,
  ): Promise<any> => {
    const response = await api.post('/dlp/report/summary', {
      period,
      companyName,
      summaryContent,
    });
    return response.data;
  },

  // 예외 처리 현황
  getExceptionStats: async (period: string, companyName: string): Promise<Record<string, number>> => {
    const response = await api.get<Record<string, number>>('/dlp/exception-stats', {
      params: { period, companyName },
    });
    return response.data;
  },

  // enum 목록
  getEnumExceptList: async (): Promise<any[]> => {
    const response = await api.get<any[]>('/dlp/enum-except-list');
    return response.data;
  },

  // 보안 USB 등록 현황
  getRegisteredMediaCount: async (period: string, companyName: string): Promise<number | null> => {
    const response = await api.get<{ regCnt: number | null }>('/dlp/registered-media-count', {
      params: { period, companyName },
    });
    return response.data.regCnt;
  },

  // 매체 첨부 1000건 이상 사용자 (세아베스틸만)
  getMediaAttachmentUsersOver1000: async (
    period: string,
    companyName: string,
  ): Promise<Array<{ department: string; name: string; count: number }>> => {
    const response = await api.get<Array<{ department: string; name: string; count: number }>>(
      '/dlp/media-attachment-users-over1000',
      {
        params: { period, companyName },
      },
    );
    return response.data;
  },

  // 3개월 미사용 USB 내역
  getUnusedUsb3Months: async (
    period: string,
    companyName: string,
  ): Promise<Array<{ id?: number; department: string; name: string; usbName: string; memo: string; displayYn: string }>> => {
    const response = await api.get<
      Array<{ id?: number; department: string; name: string; usbName: string; memo: string; displayYn: string }>
    >('/dlp/unused-usb-3months', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 3개월 미사용 USB display_yn 토글
  toggleUnusedUsbDisplay: async (id: number): Promise<void> => {
    await api.patch(`/dlp/unused-usb-3months/${id}/toggle-display`);
  },

  // 파일 첨부/차단 건수 조회
  getFileAttachmentSummary: async (
    period: string,
    companyName: string,
  ): Promise<{ attachmentCount: number; blockCount: number }> => {
    const response = await api.get<{ attachmentCount: number; blockCount: number }>(
      '/dlp/file-attachment-summary',
      {
        params: { period, companyName },
      },
    );
    return response.data;
  },

  // 예외처리 관리
  // 페이지네이션(2026-09-17) — 필터 없이 전체 조회 시 운영 데이터 수천 건이 한 번에
  // 내려와 화면이 느려지던 문제. page/limit 미지정 시 서버 기본값(1페이지/20건).
  getExceptionRequests: async (filters?: {
    exceptGroup?: string;
    reqCompany?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortKey?: string;
    sortDir?: 'asc' | 'desc';
  }): Promise<{ items: DlpExceptionRequest[]; total: number }> => {
    const response = await api.get<{ items: DlpExceptionRequest[]; total: number }>(
      '/dlp/exception-requests',
      { params: filters },
    );
    return response.data;
  },

  getExceptionRequest: async (id: number): Promise<DlpExceptionRequest> => {
    const response = await api.get<DlpExceptionRequest>(`/dlp/exception-requests/${id}`);
    return response.data;
  },

  createExceptionRequest: async (data: Partial<DlpExceptionRequest>): Promise<DlpExceptionRequest> => {
    const response = await api.post<DlpExceptionRequest>('/dlp/exception-requests', data);
    return response.data;
  },

  updateExceptionRequest: async (
    id: number,
    data: Partial<DlpExceptionRequest>,
  ): Promise<DlpExceptionRequest> => {
    const response = await api.patch<DlpExceptionRequest>(`/dlp/exception-requests/${id}`, data);
    return response.data;
  },

  deleteExceptionRequest: async (id: number): Promise<void> => {
    await api.delete(`/dlp/exception-requests/${id}`);
  },

  // reqCompany(신청자 계열사)에 따라 홀딩스/제강 그룹이 갈린다 (IPT-619). 미지정 시 기존 홀딩스 목록.
  getExceptionGroups: async (reqCompany?: string): Promise<string[]> => {
    const response = await api.get<string[]>('/dlp/exception-groups', {
      params: reqCompany ? { reqCompany } : {},
    });
    return response.data;
  },

  getExceptionDetails: async (group?: string, reqCompany?: string): Promise<any[]> => {
    const response = await api.get<any[]>('/dlp/exception-details', {
      params: { ...(group ? { group } : {}), ...(reqCompany ? { reqCompany } : {}) },
    });
    return response.data;
  },

  getExceptionRequestsByPeriodAndCompany: async (
    period: string,
    companyName: string,
  ): Promise<DlpExceptionRequest[]> => {
    const response = await api.get<DlpExceptionRequest[]>('/dlp/exception-requests-by-period', {
      params: { period, companyName },
    });
    return response.data;
  },

  uploadExceptionRequestsExcel: async (
    file: File,
  ): Promise<{ success: boolean; message: string; errors?: string[]; insertedCount?: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ success: boolean; message: string; errors?: string[]; insertedCount?: number }>(
      '/dlp/exception-requests/upload-excel',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  // 파일 첨부 TOP 5
  getFileAttachmentTop5: async (
    period: string,
    companyName: string,
  ): Promise<FileAttachmentTop5[]> => {
    const response = await api.get<FileAttachmentTop5[]>('/dlp/file-attachment-top5', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 파일 차단 건수 프로그램 TOP 5
  getFileBlockTop5: async (
    period: string,
    companyName: string,
  ): Promise<FileAttachmentTop5[]> => {
    const response = await api.get<FileAttachmentTop5[]>('/dlp/file-block-top5', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 웹브라우저 첨부 URL TOP 10
  getWebAttachmentUrlTop10: async (
    period: string,
    companyName: string,
  ): Promise<WebAttachmentUrlTop10[]> => {
    const response = await api.get<WebAttachmentUrlTop10[]>('/dlp/web-attachment-url-top10', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 파일 차단 TOP 10 (웹브라우저 첨부 URL)
  getFileBlockTop10: async (
    period: string,
    companyName: string,
  ): Promise<FileBlockTop10[]> => {
    const response = await api.get<FileBlockTop10[]>('/dlp/file-block-top10', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 매체 첨부 사용자 TOP 3
  getMediaAttachmentUserTop: async (
    period: string,
    companyName: string,
  ): Promise<MediaAttachmentUserTop[]> => {
    const response = await api.get<MediaAttachmentUserTop[]>('/dlp/media-attachment-user-top', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 워터마크 해제 신청 현황
  getWatermarkRemovalCount: async (
    period: string,
    companyName: string,
  ): Promise<number> => {
    const response = await api.get<{ count: number }>('/dlp/watermark-removal-count', {
      params: { period, companyName },
    });
    return response.data.count;
  },

  // 부서 및 사용자 출력 수 TOP 3
  getDeptUserOutputTop3: async (
    period: string,
    companyName: string,
  ): Promise<DeptUserOutputTop3[]> => {
    const response = await api.get<DeptUserOutputTop3[]>('/dlp/dept-user-output-top3', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 소프트웨어 차단 현황
  getSoftwareBlockCount: async (
    period: string,
    companyName: string,
  ): Promise<number> => {
    const response = await api.get<{ count: number }>('/dlp/software-block-count', {
      params: { period, companyName },
    });
    return response.data.count;
  },

  // 소프트웨어 차단 사용자 TOP 5
  getSoftwareBlockUserTop5: async (
    period: string,
    companyName: string,
  ): Promise<SoftwareBlockUserTop5[]> => {
    const response = await api.get<SoftwareBlockUserTop5[]>('/dlp/software-block-user-top5', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 카테고리 별 차단 소프트웨어 TOP 3
  getCategorySoftwareTop3: async (
    period: string,
    companyName: string,
  ): Promise<CategorySoftwareTop3[]> => {
    const response = await api.get<CategorySoftwareTop3[]>('/dlp/category-software-top3', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 웹사이트 차단 현황
  getWebsiteBlockCount: async (
    period: string,
    companyName: string,
  ): Promise<number> => {
    const response = await api.get<{ count: number }>('/dlp/website-block-count', {
      params: { period, companyName },
    });
    return response.data.count;
  },

  // 웹사이트 차단 사용자 TOP 5
  getWebsiteBlockUserTop5: async (
    period: string,
    companyName: string,
  ): Promise<WebsiteBlockUserTop5[]> => {
    const response = await api.get<WebsiteBlockUserTop5[]>('/dlp/website-block-user-top5', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 카테고리 별 차단 웹사이트 TOP 3
  getCategoryWebsiteTop3: async (
    period: string,
    companyName: string,
  ): Promise<CategoryWebsiteTop3[]> => {
    const response = await api.get<CategoryWebsiteTop3[]>('/dlp/category-website-top3', {
      params: { period, companyName },
    });
    return response.data;
  },

  // ========== 관리자 입력 관련 메서드 ==========

  // 웹사이트 접속 현황내 이슈 사이트
  getIssueSites: async (period: string, companyName: string): Promise<AdminIssueSite[]> => {
    const response = await api.get<AdminIssueSite[]>('/dlp/admin/issue-sites', {
      params: { period, companyName },
    });
    return response.data;
  },

  createIssueSite: async (data: Partial<AdminIssueSite>): Promise<AdminIssueSite> => {
    const response = await api.post<AdminIssueSite>('/dlp/admin/issue-sites', data);
    return response.data;
  },

  updateIssueSite: async (id: number, data: Partial<AdminIssueSite>): Promise<AdminIssueSite> => {
    const response = await api.patch<AdminIssueSite>(`/dlp/admin/issue-sites/${id}`, data);
    return response.data;
  },

  deleteIssueSite: async (id: number): Promise<void> => {
    await api.delete(`/dlp/admin/issue-sites/${id}`);
  },

  // 전사 공통 유해사이트 차단 내역
  getCommonBlockedSites: async (period: string, companyName: string): Promise<AdminCommonBlockedSite[]> => {
    const response = await api.get<AdminCommonBlockedSite[]>('/dlp/admin/common-blocked-sites', {
      params: { period, companyName },
    });
    return response.data;
  },

  createCommonBlockedSite: async (data: Partial<AdminCommonBlockedSite>): Promise<AdminCommonBlockedSite> => {
    const response = await api.post<AdminCommonBlockedSite>('/dlp/admin/common-blocked-sites', data);
    return response.data;
  },

  updateCommonBlockedSite: async (id: number, data: Partial<AdminCommonBlockedSite>): Promise<AdminCommonBlockedSite> => {
    const response = await api.patch<AdminCommonBlockedSite>(`/dlp/admin/common-blocked-sites/${id}`, data);
    return response.data;
  },

  deleteCommonBlockedSite: async (id: number): Promise<void> => {
    await api.delete(`/dlp/admin/common-blocked-sites/${id}`);
  },

  // 라이선스 현황
  getLicenseStat: async (period: string, companyName: string): Promise<AdminLicenseStat | null> => {
    const response = await api.get<AdminLicenseStat>('/dlp/admin/license-stat', {
      params: { period, companyName },
    });
    return response.data;
  },

  saveLicenseStat: async (data: Partial<AdminLicenseStat>): Promise<AdminLicenseStat> => {
    const response = await api.post<AdminLicenseStat>('/dlp/admin/license-stat', data);
    return response.data;
  },

  // 퇴사자 라이선스 현황
  getExEmployees: async (period: string, companyName: string): Promise<AdminExEmployee[]> => {
    const response = await api.get<AdminExEmployee[]>('/dlp/admin/ex-employees', {
      params: { period, companyName },
    });
    return response.data;
  },

  createExEmployee: async (data: Partial<AdminExEmployee>): Promise<AdminExEmployee> => {
    const response = await api.post<AdminExEmployee>('/dlp/admin/ex-employees', data);
    return response.data;
  },

  updateExEmployee: async (id: number, data: Partial<AdminExEmployee>): Promise<AdminExEmployee> => {
    const response = await api.patch<AdminExEmployee>(`/dlp/admin/ex-employees/${id}`, data);
    return response.data;
  },

  deleteExEmployee: async (id: number): Promise<void> => {
    await api.delete(`/dlp/admin/ex-employees/${id}`);
  },

  // 엑셀 업로드
  uploadExcel: async (
    file: File,
    period: string,
    companyName: string,
  ): Promise<{
    success: boolean;
    message: string;
    errors?: string[];
    uploadStatus: any;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/dlp/admin/upload-excel', formData, {
      params: { period, companyName },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 업로드 상태 조회
  getUploadStatus: async (period: string, companyName: string): Promise<any> => {
    const response = await api.get('/dlp/admin/upload-status', {
      params: { period, companyName },
    });
    return response.data;
  },

  // 최근 업로드 상태 조회 (벌크 입력용)
  getLatestUploadStatus: async (): Promise<any> => {
    const response = await api.get('/dlp/admin/latest-upload-status');
    return response.data;
  },

  // === 세아제강 계열 DLP (IPT-441) ===
  getSteelReport: async (period: string, company: string): Promise<DlpSteelReport> => {
    const response = await api.get<DlpSteelReport>('/dlp/steel/report', { params: { period, company } });
    return response.data;
  },

  getSteelAvailablePeriods: async (company: string): Promise<string[]> => {
    const response = await api.get<string[]>('/dlp/steel/available-periods', { params: { company } });
    return response.data;
  },

  refreshSteelCache: async (period: string, company: string): Promise<{ refreshed: boolean }> => {
    const response = await api.post<{ refreshed: boolean }>('/dlp/steel/refresh-cache', { period, company });
    return response.data;
  },
};

export interface FileAttachmentTop5 {
  programName?: string;
  processName: string;
  totalCount: number | null;
  category: string;
  dept1: string | number;
  dept2: string | number;
  dept3: string | number;
  dept4: string | number;
  dept5: string | number;
  isCountRow?: boolean;
}

export interface WebAttachmentUrlTop10 {
  url: string;
  count: number;
}

export interface FileBlockTop10 {
  processName: string;
  blockCount: number;
  category: string;
  dept1: string;
  dept2: string;
  dept3: string;
  dept4: string;
  dept5: string;
}

export interface MediaAttachmentUserTop {
  department: string;
  name: string;
  count: number;
}

export interface DeptUserOutputTop3 {
  currentDept: string;
  currentName: string;
  currentCount: number;
  prevDept: string;
  prevName: string;
  prevCount: number;
  isFirstInDept: boolean;
  deptRowspan: number;
}

export interface SoftwareBlockUserTop5 {
  department: string;
  name: string;
  userRanking: number;
  blockedSoftware: string;
  processName: string;
  blockCount: number;
  isFirstInUser: boolean;
  userRowspan: number;
}

export interface CategorySoftwareTop3 {
  categoryRanking: number;
  categoryName: string;
  processRanking: number;
  blockedSoftware: string;
  processName: string;
  blockCount: number;
  isFirstInCategory: boolean;
  categoryRowspan: number;
}

export interface WebsiteBlockUserTop5 {
  userRanking: number;
  department: string;
  name: string;
  urlRanking: number;
  blockedUrl: string;
  blockedWebsite: string;
  blockCount: number;
  isFirstInUser: boolean;
  userRowspan: number;
}

export interface CategoryWebsiteTop3 {
  categoryRanking: number;
  categoryName: string;
  urlRanking: number;
  blockedUrl: string;
  blockedWebsite: string;
  blockCount: number;
  isFirstInCategory: boolean;
  categoryRowspan: number;
}

export interface DlpExceptionRequest {
  id?: number;
  requestNo?: number;
  exceptGroup?: string;
  exceptDetail?: string;
  exceptDetailId?: number;
  reqCompany?: string;
  reqDepartment?: string;
  reqName?: string;
  targetCompany?: string;
  permittedIp?: string;
  permittedPc?: string;
  reason?: string;
  period?: string;
  approval?: string;
  actor?: string;
  applyYn?: string;
  applyDateDisplay?: string;
  applyDate?: string;
  endDate?: string;
  applyMethod?: string;
  remark?: string;
  createdAt?: Date;
  updatedAt?: Date;
  exceptDetailEnum?: {
    id: number;
    itemName: string;
    itemCode: number;
    itemEnum: string;
  };
}

export interface AdminIssueSite {
  id?: number;
  period: string;
  companyName: string;
  category?: string;
  url?: string;
  issueMemo?: string;
  register: string;
}

export interface AdminCommonBlockedSite {
  id?: number;
  period: string;
  companyName: string;
  category?: string;
  url?: string;
  issueMemo?: string;
  register: string;
}

export interface AdminLicenseStat {
  id?: number;
  period: string;
  companyName: string;
  register: string;
  totalAmount?: number;
  companyAmount?: number;
  companyUsedAmount?: number;
  unusedAmount?: number;
}

export interface AdminExEmployee {
  id?: number;
  period: string;
  companyName: string;
  register: string;
  name?: string;
  pcip?: string;
  verInfo?: string;
  lastLoginTime?: Date;
  nacAthenticator?: string;
}

